import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { DateRange } from "react-date-range";
import { addDays } from "date-fns";
import { enAU, ja, ko, zhCN } from "date-fns/locale";
import moment from "moment/moment";

import { image } from "src/theme";
import { LOCAL_STORAGE_LANGUAGE } from "@utils/api-requests/static-data";

import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

import {
  DropdownCalendarWrapper,
  DropdownCalenderNavigator,
} from "./CalendarStyling";

const DropdownCalendar = ({
  calendarOpen,
  setCalendarOpen,
  setValue,
  calendarRef,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const language = localStorage.getItem(LOCAL_STORAGE_LANGUAGE);

  const localization = useMemo(() => {
    switch (language) {
      case "en":
        return enAU;

      case "ko":
        return ko;

      case "ja":
        return ja;

      case "zh":
        return zhCN;

      default:
        break;
    }
  }, [language]);

  const [selectionStep, setSelectionStep] = useState(0); // 0: 시작 안함, 1: checkin 선택됨
  const [selectedDate, setSelectedDate] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 0),
      dateFormat: "ddd",
      key: "selection",
    },
  ]);

  const handleDateSelect = useCallback(
    (item) => {
      const newSelection = item.selection;

      if (selectionStep === 0) {
        //  checkin
        setSelectedDate([{ ...newSelection, endDate: newSelection }]);
        setSelectionStep(1);
        setValue(
          "startdate",
          moment(newSelection.startDate).format("YYYY-MM-DD")
        );
        setValue("enddate", null);
      } else {
        // checkout
        setSelectedDate([newSelection]);
        setSelectionStep(0);
        setValue(
          "startdate",
          moment(newSelection.startDate).format("YYYY-MM-DD")
        );
        setValue("enddate", moment(newSelection.endDate).format("YYYY-MM-DD"));

        setCalendarOpen(false);
      }
    },
    [selectionStep, setCalendarOpen]
  );

  // 외부영역 클릭시 calendar close(확장성을 위해 ref대신 data-exclude 속성으로 처리)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isExcludedArea = event.target.closest('[data-exclude="true"]');

      if (!isExcludedArea) setCalendarOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    calendarOpen && (
      <DropdownCalendarWrapper
        data-exclude="true"
        ref={calendarRef}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.close.default}
          alt="close icon"
          onClick={() => setCalendarOpen(false)}
        />

        <DateRange
          showDateDisplay={false}
          showMonthAndYearPickers={false}
          fixedHeight={true}
          weekdayDisplayFormat="ccccc"
          monthDisplayFormat="MMMM yyyy"
          editableDateInputs={false}
          onChange={handleDateSelect}
          moveRangeOnFirstSelection={true}
          ranges={selectedDate}
          months={2}
          nextArrow={<div>button Next</div>}
          minDate={new Date()}
          maxDate={
            new Date(new Date().setFullYear(new Date().getFullYear() + 1))
          }
          direction={isMobile ? "vertical" : "horizontal"}
          locale={localization}
          color="#502875"
          rangeColors={["#502875", "#F1E5FD"]}
          dateDisplayFormat="MM.dd.yyyy"
          navigatorRenderer={(_, changeShownDate) => {
            return (
              <DropdownCalenderNavigator>
                <button
                  type="button"
                  onClick={() => changeShownDate(-1, "monthOffset")}
                >
                  <img
                    src={image.chevronLeftBlack.default}
                    alt="Prev Month"
                    loading="lazy"
                  />
                </button>

                <button
                  type="button"
                  onClick={() => changeShownDate(+1, "monthOffset")}
                >
                  <img
                    src={image.chevronRightBlack.default}
                    alt="Next Month"
                    loading="lazy"
                  />
                </button>
              </DropdownCalenderNavigator>
            );
          }}
        />
      </DropdownCalendarWrapper>
    )
  );
};

export default DropdownCalendar;
